import { UpdateUserDto, UserDto, UserRole, UsersControllerCreateData, UsersControllerDeleteUserData, UsersControllerFindAllData, UsersControllerFindByUuidsData, UsersControllerGetUserByEmailData, UsersControllerGetUserByPhoneData, UsersControllerGetUserByUuidData, UsersControllerGetUserStatisticsData, UsersControllerUpdateUserData, UsersControllerUploadAvatarData, UserStatus, ContentType } from '@/common/api/datasources/Api';
import { api } from '@/common/api/instance';
import { SortOrder, UserSortFieldEnum } from "@/modules/Users/hooks/useUsersTableData";

interface CreateUserDto {
    email: string;
    password?: string;
    role?: UserRole;
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    status?: UserStatus;
    birthDate?: string;
    isActive?: boolean;
}

interface PaginationParams {
    page?: number;
    size?: number;
    keyword?: string;
    role?: UserRole;
    status?: UserStatus;
    isActive?: boolean;
    sortField?: UserSortFieldEnum;
    sortOrder?: SortOrder;
    search?: string;
    sortBy?: string;
}

export const usersService = {
    getAllUsers: async (params: PaginationParams): Promise<UsersControllerFindAllData> => {
        const response = await api.users.usersControllerFindAll(params);
        return response.data;
    },

    getUserByUuid: async (userUUID: string): Promise<UsersControllerGetUserByUuidData> => {
        const response = await api.users.usersControllerGetUserByUuid(userUUID);
        return response.data;
    },

    getUserStatistics: async (): Promise<UsersControllerGetUserStatisticsData> => {
        const response = await api.users.usersControllerGetUserStatistics();
        return response.data;
    },

    updateUser: async (userUUID: string, data: UpdateUserDto): Promise<UsersControllerUpdateUserData> => {
        const response = await api.users.usersControllerUpdateUser(userUUID, data);
        return response.data;
    },

    deleteUser: async (userUUID: string, hardDelete: boolean = false): Promise<UsersControllerDeleteUserData> => {
        const response = await api.users.usersControllerDeleteUser(
            userUUID,
            { hardDelete },
            {}
        );
        return response.data;
    },

    createUser: async (data: CreateUserDto): Promise<UsersControllerCreateData> => {
        const response = await api.users.usersControllerCreate(data);
        return response.data;
    },

    findByEmail: async (email: string): Promise<UsersControllerGetUserByEmailData> => {
        const response = await api.users.usersControllerGetUserByEmail(email);
        return response.data;
    },

    findByPhone: async (phone: string): Promise<UsersControllerGetUserByPhoneData> => {
        const response = await api.users.usersControllerGetUserByPhone(phone);
        return response.data;
    },

    findByUuids: async (uuids: string[]): Promise<UsersControllerFindByUuidsData> => {
        const response = await api.users.usersControllerFindByUuids({ uuids });
        return response.data;
    },

    uploadAvatar: async ({ userUUID, file }: { userUUID: string; file: File }): Promise<UsersControllerUploadAvatarData> => {
        const formData = new FormData();
        formData.append('file', file);

        const response = await api.users.usersControllerUploadAvatar(userUUID, {
            transformRequest: [(data, headers) => {
                headers['Content-Type'] = 'multipart/form-data';
                return formData;
            }]
        });

        return response.data;
    },

    getManagers: async (): Promise<UserDto[]> => {
        const response = await api.users.usersControllerFindAll({ role: UserRole.STAFF });
        return response.data.content;
    },

    updateStatus: async (userUUID: string, status: UserStatus): Promise<UsersControllerUpdateUserData> => {
        const response = await api.users.usersControllerUpdateUser(userUUID, { status });
        return response.data;
    },

    updateRole: async (userUUID: string, role: UserRole): Promise<UsersControllerUpdateUserData> => {
        const response = await api.users.usersControllerUpdateUser(userUUID, { role });
        return response.data;
    },
};

export const useUsersQuery = () => ({
    allUsersQuery: (params: PaginationParams) => ({
        queryKey: ['users', params],
        queryFn: () => usersService.getAllUsers(params),
    }),

    userByIdQuery: (userUUID: string) => ({
        queryKey: ['users', userUUID],
        queryFn: () => usersService.getUserByUuid(userUUID),
    }),

    userStatisticsQuery: {
        queryKey: ['users', 'statistics'],
        queryFn: () => usersService.getUserStatistics(),
    },

    findByEmailQuery: (email: string) => ({
        queryKey: ['users', 'email', email],
        queryFn: () => usersService.findByEmail(email),
    }),

    findByPhoneQuery: (phone: string) => ({
        queryKey: ['users', 'phone', phone],
        queryFn: () => usersService.findByPhone(phone),
    }),

    findByUuidsQuery: (uuids: string[]) => ({
        queryKey: ['users', 'bulk', uuids],
        queryFn: () => usersService.findByUuids(uuids),
    }),

    updateUserMutation: {
        mutationFn: (params: { userUUID: string; data: UpdateUserDto }) =>
            usersService.updateUser(params.userUUID, params.data),
    },

    deleteUserMutation: {
        mutationFn: (userUUID: string, hardDelete: boolean = false) =>
            usersService.deleteUser(userUUID, hardDelete),
    },

    createUserMutation: {
        mutationFn: (data: CreateUserDto) => usersService.createUser(data),
    },

    toggleUserActiveMutation: {
        mutationFn: (params: { userUUID: string; isActive: boolean }) =>
            usersService.updateUser(params.userUUID, { isActive: params.isActive }),
    },

    changeUserRoleMutation: {
        mutationFn: (params: { userUUID: string; role: UserRole }) =>
            usersService.updateUser(params.userUUID, { role: params.role }),
    },

    changeUserStatusMutation: {
        mutationFn: (params: { userUUID: string; status: UserStatus }) =>
            usersService.updateUser(params.userUUID, { status: params.status }),
    },

    uploadAvatarMutation: {
        mutationFn: ({ userUUID, file }: { userUUID: string; file: File }) => {
            return usersService.uploadAvatar({ userUUID, file });
        },
    },
}); 